import { Subject, merge } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Input,
  Output,
  Component,
  OnInit,
  OnChanges,
  OnDestroy,
  EventEmitter,
  SimpleChanges,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { SnAbilityService } from '@lib/ability';
import { SnCurrentUser } from '@lib/auth';
import { SnTitleService } from '@lib/state/title';
import { t } from '@lib/translation';
import { SnActionConfig } from '@lib/ui/actions';
import { SnDrawerService } from '@lib/ui/drawer';
import { SnLayoutService } from '@lib/ui/layout';
import { SentryVaultComponent } from '@lib/vault';

import { SnChannelMembersDrawerContainer, ISnChannelMembersDrawerData } from '../../containers';
import { SnChannel, SnChannelSettings } from '../../models';
import { SnChannelsService, SnMembersService, SnChatService } from '../../services';
import { SnChatDialogsService } from '../../services/chat-dialogs.service';
import { ChatExportTimeComponent } from '../chat-export-time/chat-export-time.component';

@Component({
  selector: 'sn-channel-actions',
  templateUrl: './channel-actions.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnChannelActionsComponent implements OnChanges, OnDestroy, OnInit {
  @Input()
  public channel: SnChannel;

  @Output()
  public readonly channelChange = new EventEmitter<SnChannel>();

  public actions: SnActionConfig[];
  public settings: SnChannelSettings;

  private readonly _canChange: boolean;
  private readonly _canDelete: boolean;
  private readonly _canAddUser: boolean;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _drawer: SnDrawerService,
    private readonly _layout: SnLayoutService,
    private readonly _viewContainerRef: ViewContainerRef,
    private readonly _chatService: SnChatService,
    private readonly _channelsService: SnChannelsService,
    private readonly _membersService: SnMembersService,
    private readonly _chatDialogsService: SnChatDialogsService,
    private readonly _router: Router,
    private readonly _currentUser: SnCurrentUser,
    private readonly _title: SnTitleService,
    private readonly _dialog: MatDialog,
    ability: SnAbilityService,
  ) {
    const subject = 'communications/chatchannel';
    const userSubject = 'communications/chatuser';

    this._canChange = ability.can({ subject, action: 'change' });
    this._canDelete = ability.can({ subject, action: 'delete' });
    this._canAddUser = ability.can({ subject: userSubject, action: 'add' });
  }

  public ngOnInit(): void {
    merge(this._chatService.activeChannelChanged$, this._chatService.chatIsReady$)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._getSettings();
      });
    this._chatService.userIsJoined$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this._updateActions();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('channel' in changes) {
      this._updateActions();
    }
  }

  public ngOnDestroy(): void {
    this.channelChange.complete();

    this._layout.appbar.actions.clear();
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _getSettings(): void {
    this._channelsService
      .getChannelSettings(this.channel.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe((settings) => {
        this.settings = settings;
        this._updateActions();
      });
  }

  private _openMemberList(): void {
    this._drawer.open<SnChannelMembersDrawerContainer, ISnChannelMembersDrawerData>(
      SnChannelMembersDrawerContainer,
      {
        width: 320,
        data: {
          sid: this.channel.sid,
        },
      },
    );
  }

  private _openInviteUserDialog(): void {
    this._chatDialogsService
      .openInviteUserDialog({
        data: {
          sid: this.channel.sid,
          members: this.channel.users,
        },
        minWidth: '20vw',
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((channel) => {
        this._onChannelUpdate(channel);
      });
  }

  private _editChannel(): void {
    this._chatDialogsService
      .openEditChannelDialog({
        viewContainerRef: this._viewContainerRef,
        data: {
          channel: this.channel,
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((channel) => {
        this._onChannelUpdate(channel);
      });
  }

  private _deleteChannel(): void {
    this._channelsService
      .deleteChannel(this.channel.sid)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._chatService.resetActiveChannel();
        this._router.navigate(['/', 'chat']);
        this._title.setTitle('');
      });
  }

  private _resetNotifications(): void {
    this._channelsService
      .updateChannelSettings(this.channel.id, { snoozeDuration: null })
      .pipe(takeUntil(this._destroy$))
      .subscribe();
  }

  private _leaveFromChannel(): void {
    this._membersService
      .leaveFromChannel(this.channel.sid, this.channel.type)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._chatService.updateChannelList();
        this._chatService.resetActiveChannel();
        this._router.navigate(['/', 'chat']);
        this._title.setTitle('');
      });
  }

  private _onChannelUpdate(channel: SnChannel): void {
    this.channel.displayName = channel.displayName;
    this.channel.publicity = channel.publicity;
    this.channel.hideSystemNotifications = channel.hideSystemNotifications;
    this.channelChange.emit(this.channel);
  }

  private _openChatExportDialog(): void {
    const data = this.channel;
    this._dialog
      .open(ChatExportTimeComponent, { data })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        if (result?.startDatetime) {
          this._openVaultDialog(result);
        }
      });
  }

  private _openVaultDialog(data: any): void {
    this._dialog
      .open(SentryVaultComponent)
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((vaultID) => {
        const vault = vaultID.id || vaultID;
        if (vault) {
          const newStartDateTime = data.startDatetime;
          const newEndDateTime = data.endDatetime;
          const payload = {
            startDatetime: newStartDateTime,
            endDatetime: newEndDateTime,
            folderId: vaultID.id ? vaultID.id : vaultID,
          };
          this._exportChat(payload);
        }
      });
  }

  private _exportChat(data: any): void {
    this._channelsService
      .exportChatChannelToPdf(this.channel.sid, data)
      .pipe(takeUntil(this._destroy$))
      .subscribe();
  }

  private _updateActions(): void {
    if (!this.channel) {
      return;
    }
    const userIsJoined = this.channel.members.some((user) => this._currentUser.id === user.id);
    this.actions = [];
    if (userIsJoined && this.channel.isOrganizationChannel) {
      this.actions = [
        {
          icon: {
            fontSet: 'fal',
            fontIcon: 'fa-users',
          },
          label: t('communication.chat.actions.memberList'),
          collapse: true,
          click: this._openMemberList.bind(this),
        },
        {
          icon: {
            fontSet: 'fal',
            fontIcon: 'fa-user-plus',
          },
          label: t('communication.chat.actions.inviteUsers'),
          collapse: true,
          click: this._openInviteUserDialog.bind(this),
          hidden: !this._canAddUser,
        },
        {
          icon: {
            fontSet: 'fal',
            fontIcon: 'fa-pen',
          },
          label: t('communication.chat.actions.editChannel'),
          collapse: true,
          click: this._editChannel.bind(this),
          hidden: !this._canChange,
        },
        {
          icon: {
            fontSet: 'fal',
            fontIcon: 'fa-download',
          },
          label: t('communication.chat.actions.exportChannel'),
          collapse: true,
          click: this._openChatExportDialog.bind(this),
        },
        {
          icon: {
            fontSet: 'fal',
            fontIcon: 'fa-trash-alt',
          },
          label: t('communication.chat.actions.deleteChannel'),
          collapse: true,
          click: this._deleteChannel.bind(this),
          hidden: !this._canDelete,
        },
        {
          icon: {
            fontSet: 'fal',
            fontIcon: 'fa-sign-out-alt',
          },
          label: t('communication.chat.actions.leaveChannel'),
          collapse: true,
          click: this._leaveFromChannel.bind(this),
        },
      ];
    }
    if (this.channel.isUserChannel) {
      this.actions = [
        {
          icon: {
            fontSet: 'fal',
            fontIcon: 'fa-sign-out-alt',
          },
          label: t('communication.chat.actions.leaveChannel'),
          collapse: true,
          click: this._leaveFromChannel.bind(this),
        },
      ];
    }
    if (this.settings && this._chatService.getSnoozeToast(this.settings)) {
      this.actions.push({
        icon: {
          fontSet: 'fal',
          fontIcon: 'fa-bell-plus',
        },
        label: t('communication.chat.actions.toggleNotifications'),
        collapse: true,
        click: this._resetNotifications.bind(this),
        hidden: !this._chatService.getSnoozeToast(this.settings),
      });
    }
    this._layout.appbar.actions.set(this.actions);
  }
}
